import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, Pagination, SearchInput, ListItemLink, List, ListItemContent, ListTitle } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ResourceCollection, MachinePolicyResource, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import Chip from "~/components/Chips/Chip";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import MachinePolicySummary from "../MachinePolicySummary";
export interface MachinePoliciesPageProps {
    spaceId: string;
    queryParams: MachinePoliciesQueryParams;
    setQueryParams: QueryParamValuesSetter<MachinePoliciesQueryParams>;
    loaderData: MachinePoliciesPageLoaderData;
}
export interface MachinePoliciesPageLoaderData {
    machinePolicies: ResourceCollection<MachinePolicyResource>;
}
export interface MachinePoliciesQueryParams {
    machinePolicyName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function machinePoliciesPageLoader(repository: Repository, queryParams: MachinePoliciesQueryParams): Promise<MachinePoliciesPageLoaderData> {
    const machinePolicies = await getPaginatedMachinesPolicies(repository, queryParams.page, queryParams.pageSize, queryParams.machinePolicyName);
    return { machinePolicies };
}
const Title = "Machine Policies";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export function MachinePoliciesPage({ spaceId, loaderData, queryParams, setQueryParams }: MachinePoliciesPageProps) {
    const { result: machinePolicies } = usePaginatedMachinePoliciesQuery(loaderData, queryParams);
    const [debouncedMachinePolicyName, setDebouncedMachinePolicyName] = useDebouncedExternalState(queryParams.machinePolicyName, (value) => setQueryParams({ machinePolicyName: value, page: 1 }), 200);
    const addMachinePolicyPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Create Machine Policy",
        path: links.createMachinePolicyPage.generateUrl({ spaceId }),
        hasPermissions: isAllowed({
            permission: Permission.MachinePolicyCreate,
        }),
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const hideBottomPagination = machinePolicies.Items.length < 4;
    return (<PageContent header={{ title: Title, primaryAction: addMachinePolicyPageAction }} pagination={{
            ui: (<Pagination label={"Policies"} totalResults={machinePolicies.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={machinePolicies.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{ inputs: [<SearchInput autoFocus={true} value={debouncedMachinePolicyName} onChange={setDebouncedMachinePolicyName} accessibleName={"Search machine policy by name"} placeholder={"Search by name"}/>] }}>
            <BorderedContent>
                <List<MachinePolicyResource> items={machinePolicies.Items} renderRow={({ item: machinePolicy }) => <MachinePolicyListItem spaceId={spaceId} machinePolicy={machinePolicy}/>} empty={<EmptyStateNoResults />}/>
            </BorderedContent>
        </PageContent>);
}
function MachinePolicyListItem({ spaceId, machinePolicy }: {
    spaceId: string;
    machinePolicy: MachinePolicyResource;
}) {
    return (<ListItemLink href={links.machinePolicyPage.generateUrl({ spaceId, machinePolicyId: machinePolicy.Id })}>
            <ListItemContent>
                <ListTitle>
                    {machinePolicy.Name}
                    {machinePolicy.IsDefault && (<span className={defaultChipStyles}>
                            <Chip>default</Chip>
                        </span>)}
                </ListTitle>
                <MachinePolicySummary machinePolicy={machinePolicy} hideDescription={false} conciseView={false}/>
            </ListItemContent>
        </ListItemLink>);
}
function getPaginatedMachinesPolicies(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, machinePolicyName: string = "") {
    return repository.MachinePolicies.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: machinePolicyName,
    });
}
function usePaginatedMachinePoliciesQuery(initialResult: MachinePoliciesPageLoaderData, queryParams: MachinePoliciesQueryParams) {
    const { page, pageSize, machinePolicyName } = queryParams;
    return useQuery((repository) => getPaginatedMachinesPolicies(repository, page, pageSize, machinePolicyName), [page, pageSize, machinePolicyName], "Get paginated machine policies", { initialResult: initialResult.machinePolicies });
}
const defaultChipStyles = css({
    marginLeft: space[2],
});
